import { useState, useCallback, useEffect, useMemo } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import {
  useButtonPressedCallback,
  useModalDisplayedEvent
} from '@zenbusiness/application-commons-clickstream';
import { useSlideCheckout } from '@zenbusiness/application-commons-ecommerce';
import {
  useExperimentTrackCallback,
  useExperimentFlag
} from '@zenbusiness/application-commons-experiments';
import { useQuery, useMutation, useManualQuery } from '@zenbusiness/application-commons-graphql';
import isFalsey from '@zenbusiness/application-commons-utils/isFalsey';
import isTrue from '@zenbusiness/application-commons-utils/isTrue';
import isUndefinedOrNull from '@zenbusiness/application-commons-utils/isUndefinedOrNull';
import PageLoading from '@zenbusiness/zui/PageLoading';
import dayjs from 'dayjs';
import intersection from 'lodash/intersection';
import PropTypes from 'prop-types';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import StepsModal from '#client/components/StepsModal';
import {
  ACCOUNT_PURPOSES_REQUIRING_DETAIL,
  SOURCES_OF_FUNDS_REQUIRING_DESCRIPTION,
  TRANSACTION_VOLUMES_REQUIRING_DESCRIPTION
} from '#client/constants/additionalInfo';
import { DB_APPLICATION_STATUS } from '#client/constants/application';
import { PRODUCT_CATALOG_SERVICES } from '#client/constants/catalogServices';
import { DATE_FORMAT } from '#client/constants/date';
import { RELEASE_THREAD_BANK_COMPLIANCE } from '#client/constants/experimentsFlags';
import { MODAL_NAMES } from '#client/constants/modals';
import { BASE_URL } from '#client/constants/url';
import { BANKING_ELIGIBILITY_STATUS } from '#client/constants/validationMessages';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useBankingEligibilityCheck from '#client/hooks/useBankingEligibilityCheck';
import useBankingPath from '#client/hooks/useBankingPath';
import useBankingPreferredOrderStatus from '#client/hooks/useBankingPreferredOrderStatus';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useEINOrderStatus from '#client/hooks/useEINOrderStatus';
import { useLogError } from '#client/hooks/useLogError';
import useMemoObject from '#client/hooks/useMemoObject';
import useMfaStatus from '#client/hooks/useMfaStatus';
import useModalContext from '#client/hooks/useModalContext';
import useSessionStorage from '#client/hooks/useSessionStorage';
import useToastContext from '#client/hooks/useToastContext';
import useValidateApplication from '#client/hooks/useValidateApplication';
import { ENABLE_MFA_MUTATION, PATCH_APPLICATION_INIT_DATA_MUTATION } from '#client/mutations';
import {
  BANKING_PREFERRED_ORDER_STATUS,
  GET_APPLICATION_INIT_DATA_QUERY,
  VALIDATE_APPLICATION
} from '#client/queries';
import { checkIsMobile } from '#client/utils/devices';

import ModalTitleSaved from './components/ModalTitle';
import { MoreInfo, DataWeHave, ValidationMessage, ApplicationIntro } from './components/Steps';

import { checkProhibitedBusinessIndustry } from './components/CustomFields/utils/prohibit';
import { ModelMetadata, AccountMembersMetadata } from './components/Metadata/Metadata';
import {
  NOT_ALLOW_ACCESS_STATUS_LIST,
  ALLOW_REFETCH_STATUS_LIST,
  BENEFICIAL_OWNER_REQUIRED_VALIDATE_FIELDS,
  BUSINESS_ENTITY_REQUIRED_VALIDATE_FIELDS,
  FIELD_KEYS
} from './constants/statusAndField';
import { useForceUpdate } from './hooks/useForceUpdate';
import { getInvalidFieldsByType } from './utils/validateRequiredFields';

const isDesktop = !checkIsMobile();

export default function OpenAccountApplicationModal({ onClose }) {
  const forceUpdate = useForceUpdate();
  const { login } = useAuthentication();
  const { isEnabled, shouldEnableMfaFirst, isFetchingMfaStatus } = useMfaStatus();
  const { createCommonErrorToast, clearAllToast } = useToastContext();
  const { closeModal } = useModalContext();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const [loading, setLoading] = useState(false);
  const [applicationDataId, setApplicationDataId] = useState(null);
  const [isProhibitedBusinessIndustry, setIsProhibitedBusinessIndustry] = useState(false);
  const [shouldShowChaseModalIfIsProhibited, setShouldShowChaseModalIfIsProhibited] =
    useState(false);
  const { openCheckout, closeCheckout } = useSlideCheckout();

  const trackExperiment = useExperimentTrackCallback();
  const isThreadBankReleased = useExperimentFlag(RELEASE_THREAD_BANK_COMPLIANCE, false);

  const [patchApplicationData] = useMutation(PATCH_APPLICATION_INIT_DATA_MUTATION, {
    noThrow: true
  });
  const [enableMFA] = useMutation(ENABLE_MFA_MUTATION, {
    noThrow: true
  });
  const [checkAndUpdateBankingOrderStatus] = useManualQuery(BANKING_PREFERRED_ORDER_STATUS, {
    noThrow: true
  });
  const bankingPath = useBankingPath();
  const [validateApplication] = useManualQuery(VALIDATE_APPLICATION, { noThrow: true });

  // The flag to checking for first time render step on DOM
  const [isFirstTime, setIsFirstTime] = useState(true);

  /* query banking-api applications/status/${input.businessUuid}?dbOnly=true
  to retrieve applicationDataId */
  const {
    applicationDataIdResult,
    appStatusError,
    appStatus,
    refetchAppStatus,
    loading: fetchingAppStatus
  } = useApplicationStatusContext();

  if (applicationDataIdResult && applicationDataId === null) {
    setApplicationDataId(applicationDataIdResult);
  }

  const {
    bankingEligibilityStatus,
    isCheckingEligibleBankingApplication,
    eligibleBankingApplicationError
  } = useBankingEligibilityCheck({});

  const isBankingEligible = bankingEligibilityStatus === BANKING_ELIGIBILITY_STATUS.ELIGIBLE;
  const blockApplicationInit =
    isCheckingEligibleBankingApplication || !!eligibleBankingApplicationError || !isBankingEligible;
  const skipCallApplicationInitData =
    !businessEntityUuid ||
    isFalsey(isEnabled) ||
    !appStatus ||
    NOT_ALLOW_ACCESS_STATUS_LIST.includes(appStatus) ||
    blockApplicationInit;

  const {
    data: originalData,
    error: applicationInitError,
    errors,
    refetch: refetchApplicationInit
  } = useQuery(GET_APPLICATION_INIT_DATA_QUERY, {
    variables: {
      input: {
        businessUuid: businessEntityUuid
      }
    },
    skip: skipCallApplicationInitData,
    skipCache: true,
    noThrow: true
  });

  useLogError(errors, applicationInitError);
  const appData = useMemoObject(originalData);
  const data = appData?.applicationInit;
  if (applicationDataId === null && data?.applicationDataId) {
    setApplicationDataId(data.applicationDataId);
  }

  const {
    isCheckingBankingPreferredOrderStatus,
    bankingPreferredOrderStatusError,
    hasBankingPreferredOrder
  } = useBankingPreferredOrderStatus({ isSkip: !appData });

  const {
    validationData: originalValidationData,
    validateApplicationError,
    refetchValidateApplication
  } = useValidateApplication({ applicationDataId, isSkip: !appData });

  const validationData = useMemoObject(originalValidationData);
  const isBusinessAddressRA = validationData?.errors?.some(
    (error) => error.field === FIELD_KEYS.BUSINESS_ADDRESS_RA
  );
  const isOperationalAddressRA = validationData?.errors?.some(
    (error) => error.field === FIELD_KEYS.OPERATIONAL_ADDRESS_RA
  );

  /* query to zenapi /order-tasks/{order task UUID}
  to check if customer has already purchased */
  const { isCheckingEINOrderStatus, einOrderStatusError, hasEINOrder } = useEINOrderStatus({
    isSkip: !appData
  });

  const navigate = useNavigate();

  const redirectHandler = async () => {
    const { data: bankingOrderStatus, error } = await checkAndUpdateBankingOrderStatus({
      variables: {
        input: {
          businessUuid: businessEntityUuid,
          updateStatus: true // update app status to awaitingCheckout if doesn't have banking order
        }
      },
      skipCache: true
    });

    if (error || isUndefinedOrNull(bankingOrderStatus)) {
      createCommonErrorToast({ error });
      setLoading(false);
      return;
    }

    // Note: we no need refetch the app status, because
    // we'll redirect to ZenReg, after that the user goes back to the dashboard
    // the API get app status will be auto call.
    const hasBankingOrder = bankingOrderStatus?.bankingPreferredOrderStatus?.hasOrder || false;
    if (hasBankingOrder) {
      navigate(`?modal=${MODAL_NAMES.APPLICATION_RECEIVED_MODAL}`, {
        replace: true
      });
    } else {
      openCheckout({
        serviceNames: [PRODUCT_CATALOG_SERVICES.BANKING_PREFERRED],
        onClose: ({ isPurchaseComplete }) => {
          closeCheckout();
          if (isPurchaseComplete) {
            navigate(`?modal=${MODAL_NAMES.APPLICATION_RECEIVED_MODAL}`, {
              replace: true
            });
            // This to make sure the application status is loaded and avoid showing the banking checkout page on the background
            window.location.reload();
          } else {
            // Redirect to the banking dashboard
            navigate(`${bankingPath}/dashboard`, {
              replace: true
            });
          }
        }
      });

      onClose(); // close the application init modal
    }
  };

  /**
   * The API GET_APPLICATION_INIT_DATA_QUERY is also update app status from
   * `notStarted` to `started`.
   *  So we need call `refetchAppStatus` to update app status for useApplicationStatusContext()
   *  and show banner on dashboard correctly.
   *  The condition to trigger refetch should be:
   *  appStatus IN THE CONTEXT is NOT_STARTED or EXPIRED
   *  and the API GET_APPLICATION_INIT_DATA_QUERY call successful.
   */
  useEffect(() => {
    const shouldRefetchAppStatus = ALLOW_REFETCH_STATUS_LIST.includes(appStatus) && !!appData;
    if (NOT_ALLOW_ACCESS_STATUS_LIST.includes(appStatus)) {
      onClose();
    }
    if (shouldRefetchAppStatus) {
      refetchAppStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStatus, appData]);

  const dataChangeHandler = useCallback(
    async (valid, dataFields, setSaved, fieldKey) => {
      if (!valid) {
        setSaved(forceUpdate, { isSave: false });
        return;
      }

      // if industry changes, evaluate whether it is prohibited
      if (fieldKey === FIELD_KEYS.NAICS_CODE) {
        const isProhibited = checkProhibitedBusinessIndustry(dataFields?.businessIndustry);
        flushSync(() => {
          setIsProhibitedBusinessIndustry(isProhibited);
        });
      }

      for (const member of dataFields?.bankAccountMembers || []) {
        member.applicationBeneficialOwnerId =
          data?.bankAccountMembers[0].applicationBeneficialOwnerId;
      }
      const { data: patchData, error } = await patchApplicationData({
        variables: {
          input: {
            bankingApplicationId: data?.applicationId,
            ...dataFields
          }
        }
      });

      const firstErrorMessage = error?.message;

      if (error || patchData?.status === 'Fail') {
        setSaved(forceUpdate, { isSave: false, isError: true, firstErrorMessage });
        // Only show common error toast when the error is not related to operational address
        if (!firstErrorMessage || !firstErrorMessage.includes('Operational address')) {
          createCommonErrorToast({ error: firstErrorMessage || error });
        }
      } else {
        // Due to race condition between the application validation request
        // and the final initialization request, we should await the refetchApplicationInit()
        await refetchApplicationInit();

        setSaved(() => flushSync(() => forceUpdate()), { isSave: true });
        // If the user updated ein value, then should refetch validation API
        if (fieldKey === FIELD_KEYS.EIN) {
          refetchValidateApplication({
            variables: {
              input: {
                applicationDataId: data?.applicationDataId,
                businessUuid: businessEntityUuid
              }
            }
          });
        }
      }
    },
    [
      data?.applicationId,
      patchApplicationData,
      data?.applicationDataId,
      data?.bankAccountMembers,
      forceUpdate,
      createCommonErrorToast,
      refetchApplicationInit,
      refetchValidateApplication,
      businessEntityUuid
    ]
  );

  useEffect(() => {
    // Clear all the toasts when opening application init modal
    clearAllToast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // will close modal when API error
    const errorMessage =
      appStatusError ||
      validateApplicationError ||
      einOrderStatusError ||
      bankingPreferredOrderStatusError ||
      applicationInitError ||
      eligibleBankingApplicationError;

    if (errorMessage) {
      createCommonErrorToast({ error: errorMessage });
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    validateApplicationError,
    applicationInitError,
    appStatusError,
    onClose,
    bankingPreferredOrderStatusError,
    einOrderStatusError,
    bankingPreferredOrderStatusError,
    eligibleBankingApplicationError
  ]);

  const { bankAccountMembers, ...applicationInitData } = data || {};
  const flattedData = {
    ...applicationInitData,
    ...bankAccountMembers?.[0],
    agreements: ModelMetadata.agreements.ref?.current?.checked
  };

  const fieldsToRender = useMemo(() => {
    const EDDFields = [
      { field: ModelMetadata.annualRevenue },
      { field: ModelMetadata.numberOfEmployees },
      { field: ModelMetadata.cashFlow },
      ...(!isThreadBankReleased ? [{ field: ModelMetadata.country }] : [])
    ];

    const EDDFieldsArray = EDDFields.map(({ field }) => field);

    let fields = [
      { field: AccountMembersMetadata.ownsSeventyFiveOrMore },
      { field: AccountMembersMetadata.ssn },
      { field: ModelMetadata.ein },
      { field: AccountMembersMetadata.address },
      { field: AccountMembersMetadata.addressV2 },
      { field: ModelMetadata.businessAddress },
      { field: ModelMetadata.businessAddressRA },
      { field: ModelMetadata.operationalAddress },
      { field: ModelMetadata.operationalAddressRA },
      { field: ModelMetadata.cardMailingAddress },
      ...(isThreadBankReleased ? [{ field: ModelMetadata.country }] : []),
      { field: ModelMetadata.naicsCode },
      { field: AccountMembersMetadata.dateOfBirth },
      { field: ModelMetadata.businessWebsiteUrl },
      ...(isThreadBankReleased
        ? [
            { field: ModelMetadata.sourceOfFunds },
            { field: ModelMetadata.accountPurpose },
            { field: ModelMetadata.entityType },
            { field: ModelMetadata.transactionVolume },
            { field: ModelMetadata.regulatedStatus },
            { field: ModelMetadata.usNexus }
          ]
        : []),
      ...EDDFields,
      { field: ModelMetadata.agreements, noLabel: true }
    ];

    if (hasEINOrder) {
      fields = fields.filter(({ field }) => field !== ModelMetadata.ein);
    }

    if (flattedData.operationalAddressLine1 && !isOperationalAddressRA) {
      fields = fields.filter(({ field }) => field !== ModelMetadata.operationalAddressRA);
      fields = fields.filter(({ field }) => field !== ModelMetadata.businessAddress);
      fields = fields.filter(({ field }) => field !== ModelMetadata.businessAddressRA);
    }

    if (flattedData.operationalAddressLine1 && isOperationalAddressRA) {
      fields = fields.filter(({ field }) => field !== ModelMetadata.operationalAddressRA);
      fields = fields.filter(({ field }) => field !== ModelMetadata.businessAddress);
      fields = fields.filter(({ field }) => field !== ModelMetadata.businessAddressRA);
    }

    if (!flattedData.operationalAddressLine1 && !isBusinessAddressRA) {
      fields = fields.filter(({ field }) => field !== ModelMetadata.businessAddressRA);
      fields = fields.filter(({ field }) => field !== ModelMetadata.operationalAddress);
      fields = fields.filter(({ field }) => field !== ModelMetadata.operationalAddressRA);
    }

    if (!flattedData.operationalAddressLine1 && isBusinessAddressRA) {
      fields = fields.filter(({ field }) => field !== ModelMetadata.businessAddress);
      fields = fields.filter(({ field }) => field !== ModelMetadata.operationalAddress);
      fields = fields.filter(({ field }) => field !== ModelMetadata.operationalAddressRA);
    }

    fields = fields.filter(
      ({ field }) =>
        field !== AccountMembersMetadata.address && field !== ModelMetadata.cardMailingAddress
    );

    // Remove EDD fields and set saved status to false
    if (flattedData.ssnLast || (!flattedData.nationality && !flattedData.passport)) {
      fields = fields.filter(({ field }) => {
        for (const EDDField of EDDFieldsArray) {
          if (field.key === EDDField.key) {
            return false;
          }
        }
        return true;
      });

      EDDFieldsArray.forEach((field) => {
        field.setSaved(false, false, false);
      });
    }

    return fields;
  }, [
    flattedData.nationality,
    flattedData.passport,
    flattedData.ssnLast,
    flattedData.operationalAddressLine1,
    hasEINOrder,
    isBusinessAddressRA,
    isOperationalAddressRA,
    isThreadBankReleased
  ]);

  // update save status of fields
  const updateSaveStatus = () => {
    if (data) {
      const {
        ownsSeventyFiveOrMore,
        ssnLast,
        nationality,
        passport,
        einAnswer,
        addressLine1,
        city,
        state,
        zip,
        country,
        dateOfBirth,
        businessWebsiteUrl,
        noWebsite,
        annualRevenue,
        countriesOfOperation,
        cashFlow,
        numberOfEmployees,
        cardMailingAddress,
        entityType,
        accountPurpose,
        accountPurposeDetail,
        sourceOfFunds,
        sourceOfFundsDescription,
        transactionVolume,
        transactionVolumeDescription,
        isRegulated,
        regulatorName,
        usNexus,
        businessIndustry
      } = flattedData;

      const saveStatuses = {
        ownsSeventyFiveOrMore: Boolean(ownsSeventyFiveOrMore),
        ssn: Boolean(ssnLast) || Boolean(nationality && passport),
        naicsCode: Boolean(businessIndustry),
        countriesOfOperation: Boolean(countriesOfOperation),
        ein: Boolean(einAnswer),
        annualRevenue: Boolean(annualRevenue),
        address: [addressLine1, city, state, zip, country].every(Boolean),
        dateOfBirth: dayjs(dateOfBirth, DATE_FORMAT.BIRTHDAY).isValid(),
        businessWebsiteUrl: Boolean(noWebsite) || Boolean(businessWebsiteUrl),
        cashFlow: Boolean(cashFlow),
        numberOfEmployees: Boolean(numberOfEmployees),
        cardMailingAddress: Boolean(cardMailingAddress),
        entityType: Boolean(entityType),
        accountPurpose:
          Boolean(accountPurpose) &&
          (!ACCOUNT_PURPOSES_REQUIRING_DETAIL.includes(accountPurpose) ||
            Boolean(accountPurposeDetail)),
        sourceOfFunds:
          Boolean(sourceOfFunds) &&
          (!SOURCES_OF_FUNDS_REQUIRING_DESCRIPTION.includes(sourceOfFunds) ||
            Boolean(sourceOfFundsDescription)),
        transactionVolume:
          Boolean(transactionVolume) &&
          (!TRANSACTION_VOLUMES_REQUIRING_DESCRIPTION.includes(transactionVolume) ||
            Boolean(transactionVolumeDescription)),
        regulatorName: isRegulated === false || (isRegulated === true && Boolean(regulatorName)),
        usNexus: Boolean(usNexus)
      };

      for (const { field } of fieldsToRender) {
        field.setSaved(saveStatuses[field.key]);
      }

      forceUpdate();
    }
  };

  // Update save status when data is changed
  useEffect(() => {
    updateSaveStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!data]);

  const loginCall = useCallback(
    () => login(`${bankingPath}/dashboard?modal=${MODAL_NAMES.APPLICATION_INIT_MODAL}`),
    [login, bankingPath]
  );
  const redirectToMFA = useCallback(async () => {
    // loading until the Universal login page trigger
    setLoading(true);
    if (shouldEnableMfaFirst) {
      await enableMFA();
    }
    // we'll redirect to Universal login page, then redirect to application init
    // so we no need to reset the active step anymore
    loginCall();
  }, [loginCall, enableMFA, shouldEnableMfaFirst]);

  // check if all fields are saved and if the agreements are checked
  const isAllFieldSaved =
    fieldsToRender.every(
      ({ field }) => (field.saved || !field.require) && field.valid && !field.loading
    ) && ModelMetadata.agreements.ref?.current?.checked === true;

  const trackLetsGoClick = useButtonPressedCallback({
    elementName: 'banking_mfa_start',
    elementText: "Let's go",
    appPageName: 'banking_application'
  });
  const ApplicationIntroStep = {
    StepContent: <ApplicationIntro />,
    NextButton: {
      Text: "Let's go",
      OnClick: async () => {
        await redirectToMFA();
        /**
         * Tracking Details: Tracking to be added to the Banking MFA intro screen.
         * Question: What % of traffic is coming from each source pointing to this page?
         */
        trackLetsGoClick();
      },
      Loading: loading
    },
    /**
     * Tracking Details: Tracking to be added to the Banking MFA intro screen.
     * Question: What % of traffic is coming from each source pointing to this page?
     */
    TrackingEvent: {
      modalName: 'banking_mfa_start'
    },
    actionButtonType: ACTION_BUTTON_TYPE.FULL_WIDTH
  };

  const markAsRendered = useCallback(() => {
    setIsFirstTime(false);
  }, []);

  const invalidFieldsRetrieved = useMemo(() => {
    // get all invalid fields by type
    // the result is include the invalid fields, which using for both Review and Collect modal.
    const invalidBeneficialOwnerFields = getInvalidFieldsByType(
      validationData?.errors || [],
      'beneficialOwner'
    );
    const invalidBusinessEntityFields = getInvalidFieldsByType(
      validationData?.errors || [],
      'application'
    );

    return {
      // get the invalid fields, which using for Review modal,
      // but not exits on Collect modal for beneficialOwner/businessEntity
      forBeneficialOwner: intersection(
        BENEFICIAL_OWNER_REQUIRED_VALIDATE_FIELDS,
        invalidBeneficialOwnerFields
      ),
      forBusinessEntity: intersection(
        BUSINESS_ENTITY_REQUIRED_VALIDATE_FIELDS,
        invalidBusinessEntityFields
      )
    };
  }, [validationData?.errors]);

  const getErrorFields = useCallback(
    (invalidFields) => {
      // no ein number (missing ein) => remove the `ein` field out of list
      // we don't disable CTA when ein is missing => the invalidFields should exclude `ein` field
      // Incase EIN has value but it invalid => the `invalidFields` should include the `ein` field
      const shouldRemoveEINField = data && !data.ein;
      if (shouldRemoveEINField) {
        return invalidFields.filter((field) => field !== FIELD_KEYS.EIN);
      }
      return invalidFields;
    },
    [data]
  );

  // Note: SHOULD NOT add getErrorFields as a dependency.
  // Because the EIN data will be updated when the users change on CollectInfo modal.
  // It leads to the logic checking disabled CTA not correctly => display wrong modal
  // we should only rely on `data` on the first call (application init)
  // we don't worry about validation errors, it only call 1 time.
  const isDisabledCTADataWeHave = useMemo(() => {
    const beneficialOwnerHasErrors = invalidFieldsRetrieved.forBeneficialOwner.length > 0;
    const businessEntityErrorFields = getErrorFields(invalidFieldsRetrieved.forBusinessEntity);

    return beneficialOwnerHasErrors || businessEntityErrorFields.length > 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidFieldsRetrieved.forBeneficialOwner.length, invalidFieldsRetrieved.forBusinessEntity]);

  const trackNextClick = useButtonPressedCallback({
    elementName: 'next_button_on_banking_review_info',
    elementText: 'Next',
    appPageName: 'banking_review_info'
  });
  const DataWeHaveStep = {
    StepContent: (
      <DataWeHave
        data={data || {}}
        hasEINOrder={hasEINOrder}
        invalidFields={invalidFieldsRetrieved}
        isFirstTime={isFirstTime}
        isShowMissingBanner={isDisabledCTADataWeHave}
        markAsRendered={markAsRendered}
      />
    ),
    NextButton: {
      OnClick: () => {
        updateSaveStatus();
        /**
         * Tracking Details: Tracking to be added to the Banking Application Review Info screen.
         * Question: What % of traffic is coming from each source pointing to this page?
         */
        trackNextClick();
      },
      Text: 'Next',
      // should disable the next button when we have any invalid fields
      // or in the first time render
      Disabled: isFirstTime || isDisabledCTADataWeHave
    },
    /**
     * Tracking Details: Tracking to be added to the Banking Application Review Info screen.
     * Question: What % of traffic is coming from each source pointing to this page?
     */
    TrackingEvent: {
      modalName: 'banking_review_info'
    },
    actionButtonType: ACTION_BUTTON_TYPE.PRIMARY
  };

  const trackSubmitClick = useButtonPressedCallback({
    elementName: 'next_button_on_banking_collect_info',
    elementText: 'Next',
    appPageName: 'banking_collect_info'
  });
  const handleSubmitBankingApplication = async () => {
    // if business is prohibited and LD is enabled
    if (isProhibitedBusinessIndustry) {
      setLoading(true);

      // this function will update application status to notQualified
      await validateApplication({
        variables: {
          input: {
            applicationDataId,
            businessUuid: businessEntityUuid
          },
          useCache: false
        }
      });

      // show chase modal if business is prohibited
      flushSync(() => setShouldShowChaseModalIfIsProhibited(true));
      setLoading(false);
    } else {
      /**
       * Tracking Details: Tracking to be added to the banking application Collect Info screen.
       * Question:
       *          Where are customers typically dropping off on this screen?
       *          Do they typically drop off at SSN?
       *          How often are they viewing the user agreements?
       */
      trackSubmitClick();
      trackExperiment('Banking application submitted');
      setLoading(true);

      // We need to close the banking upsell modal appear when the dashboard version is variant B
      closeModal(MODAL_NAMES.BANKING_UPSELL);
      redirectHandler();
    }
  };

  const MoreInfoStep = {
    StepContent: (
      <MoreInfo
        data={flattedData}
        dataChangeHandler={dataChangeHandler}
        fieldsToRender={fieldsToRender}
        shouldShowChaseModalIfIsProhibited={shouldShowChaseModalIfIsProhibited}
      />
    ),
    // Only show the scroll bar on desktop
    showScroll: isDesktop,
    NextButton: shouldShowChaseModalIfIsProhibited
      ? null
      : {
          Text: 'Next',
          OnClick: () => {
            handleSubmitBankingApplication();
          },
          Disabled: !isAllFieldSaved,
          Loading: loading
        },
    /**
     * Tracking Details: Tracking to be added to the banking application Collect Info screen.
     * Question:
     *          Where are customers typically dropping off on this screen?
     *          Do they typically drop off at SSN?
     *          How often are they viewing the user agreements?
     */
    TrackingEvent: {
      modalName: 'banking_collect_info'
    },
    actionButtonType: ACTION_BUTTON_TYPE.FULL_WIDTH,
    isInlineButton: true
  };

  const introStep = [ApplicationIntroStep];
  const reviewAndCollectSteps = [DataWeHaveStep, MoreInfoStep];
  const collectInfoStep = [MoreInfoStep];

  /*
    Validations
  */
  const isFormationOrderIncomplete =
    bankingEligibilityStatus === BANKING_ELIGIBILITY_STATUS.FORMATION_ORDER_INCOMPLETE;
  const ctaText = isFormationOrderIncomplete ? 'Finish Formation' : 'Got it';

  const trackCtaClick = useButtonPressedCallback({
    elementName: 'banking_not_eligible_got_it',
    elementText: ctaText,
    appPageName: 'banking_application'
  });
  const ValidationMessageStep = {
    StepContent: <ValidationMessage validationStatus={bankingEligibilityStatus} />,
    NextButton: isFormationOrderIncomplete
      ? {
          Text: ctaText,
          OnClick: () => {
            trackCtaClick();
            window.location.replace(`${BASE_URL}/dashboard`);
          },
          Loading: loading
        }
      : null,
    TrackingEvent: {
      modalName: 'banking_validation_message'
    }
  };
  const validationMessageStep = [ValidationMessageStep];
  const displaySteps = () => {
    if (!isBankingEligible) {
      return validationMessageStep;
    }
    // if mfa disabled and (variantion 2 or control) => we should show intro step
    if (isFalsey(isEnabled)) {
      return introStep;
    }
    if (!isDisabledCTADataWeHave && hasBankingPreferredOrder) {
      return collectInfoStep;
    }
    return reviewAndCollectSteps;
  };

  // exclude the `isValidateApplication` because the content
  // will be reload (show loading animation) when update EIN field on CollectInfo step
  // we call refetchValidateApplication
  const isFetchingData =
    fetchingAppStatus ||
    isFetchingMfaStatus ||
    isCheckingEINOrderStatus ||
    isCheckingBankingPreferredOrderStatus ||
    isCheckingEligibleBankingApplication;

  let isDisplayLoading;

  if (
    !isCheckingEligibleBankingApplication &&
    !eligibleBankingApplicationError &&
    !isBankingEligible
  ) {
    isDisplayLoading = false;
  } else {
    isDisplayLoading =
      isFetchingData ||
      (data && hasBankingPreferredOrder === undefined) ||
      (data && hasEINOrder === undefined) ||
      // this condition will handle show loading in the fist time
      (data && validationData === undefined) ||
      (!data && isTrue(isEnabled));
  }

  const steps = isDisplayLoading
    ? [
        {
          StepContent: <PageLoading />
        }
      ]
    : displaySteps();

  const [, setStopRedirectToBankingApplication] = useSessionStorage(
    `stop_redirect_banking_application.${businessEntityUuid}`,
    false
  );

  const trackModalCloseClick = useButtonPressedCallback({
    elementName: 'banking_application_closed',
    elementText: 'cross_pressed',
    appPageName: 'banking_application'
  });
  const modalCloseHandler = () => {
    trackModalCloseClick();

    // Stop auto redirect to application init when closing modal
    if ([DB_APPLICATION_STATUS.NOT_STARTED, DB_APPLICATION_STATUS.INCOMPLETE].includes(appStatus)) {
      setStopRedirectToBankingApplication(true);
    }
    onClose();
  };
  const modalConfig = {
    title: (
      <ModalTitleSaved
        saved={isAllFieldSaved}
        text="Open a business bank account"
      />
    ),
    ctaButtonsWithOpacity: isFalsey(isEnabled),
    isHideHeader: isDisplayLoading,
    onClose: modalCloseHandler,
    disableBack: isProhibitedBusinessIndustry,
    steps
  };

  useModalDisplayedEvent({
    open: !isDisplayLoading && isBankingEligible && !isFalsey(isEnabled),
    elementName: 'banking_review_info',
    elementText: 'banking_review_info',
    appPageName: 'banking_application'
  });

  return <StepsModal {...modalConfig} />;
}

OpenAccountApplicationModal.propTypes = {
  onClose: PropTypes.func.isRequired
};
